import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Box,
  TrendingUp,
  Package,
  Store,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  ClipboardCheck
} from "lucide-react";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import ExportData from "../../components/common/ExportData";
import RoleBasedComponent from "../../components/RoleBasedComponent";

const ReorderLevel = () => {
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();

  const [inventoryList, setInventoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const fetchInventory = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/0?page=${current_page}&pageSize=${page_size}&search=${search_query}`,
        "GET"
      );
      setInventoryList(data?.results || []);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / page_size));
    } catch (error) {
      console.error("Error fetching inventory data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (page_size, search_query, current_page) => {
    try {
      const data = await requestWithAuth(
        `/inventory/branch/0?page=${current_page}&pageSize=10000000000000000000000000&search=${search_query}`,
        "GET"
      );
      const formattedData = data?.results?.map((item) => ({
        ID: item?.drug?.id,
        Drug: item?.drug?.name,
        Quantity: item?.quantity,
        "Reorder Level": item?.reorder_level,
        "Selling Price": item?.selling_price,
      }));
      setTableData(formattedData || []);
    } catch (error) {
      console.error("Error fetching export data:", error);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchInventory(pageSize, search, currentPage);
      getTableData(pageSize, search, currentPage);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, currentPage, pageSize]);

  const sortedInventory = useMemo(() => {
    let sortableItems = [...inventoryList];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'drug.name') {
          aValue = a?.drug?.name || '';
          bValue = b?.drug?.name || '';
          if (aValue.toLowerCase() < bValue.toLowerCase()) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aValue.toLowerCase() > bValue.toLowerCase()) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        } else if (sortConfig.key === 'drug.id') {
          aValue = a?.drug?.id || 0;
          bValue = b?.drug?.id || 0;
        } else if (sortConfig.key === 'selling_price') {
          // selling_price is sometimes in drug object depending on API, but check original code:
          // Original: data?.results[i]?.drug?.selling_price
          // Wait, original map used item?.selling_price for display but item?.drug?.selling_price for export?
          // Original table: inventory?.drug?.selling_price. 
          // Let's use that.
          aValue = parseFloat(a?.drug?.selling_price || 0);
          bValue = parseFloat(b?.drug?.selling_price || 0);
        } else {
          aValue = parseFloat(a[sortConfig.key] || 0);
          bValue = parseFloat(b[sortConfig.key] || 0);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [inventoryList, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleNextPage = () => {
    if (nextPage) setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (prevPage) setCurrentPage((prev) => prev - 1);
  };

  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);

  const formatCurrency = (amount) => {
    return `GHS ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (num) => {
    return Number(num || 0).toLocaleString('en-US');
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;
    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) pageNumbers.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[32px] h-[32px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-600 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} className="text-gray-400 ml-1" />;
    }
    return sortConfig.direction === 'asc' ?
      <ArrowUp size={14} className="text-blue-600 ml-1" /> :
      <ArrowDown size={14} className="text-blue-600 ml-1" />;
  };

  const renderSortableHeader = (label, key, align = "left") => (
    <th
      className={`text-${align} py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none`}
      onClick={() => handleSort(key)}
    >
      <div className={`flex items-center ${align === "right" ? "justify-end" : "justify-start"}`}>
        {label}
        <SortIcon columnKey={key} />
      </div>
    </th>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <span>Branch</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Inventory Stock</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inventory Stock List</h1>
              <p className="text-gray-600 text-sm">
                {activeSession?.branch?.name}
              </p>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Controls */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search inventory..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Page Size & Actions */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                <div className="flex">
                  <ExportData tableData={tableData} fileName={`Inventory Stock List - ${activeSession?.branch?.name}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span>Loading inventory...</span>
                </div>
              </div>
            ) : inventoryList?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {renderSortableHeader("ID", "drug.id")}
                    {renderSortableHeader("Item Name", "drug.name")}
                    {renderSortableHeader("Quantity", "quantity")}
                    {renderSortableHeader("Reorder Limit", "reorder_level")}
                    {renderSortableHeader("Selling Price", "selling_price")}
                    <th className="text-right py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedInventory.map((inventory, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-sm text-gray-600">
                        #{inventory?.drug?.id}
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {inventory?.drug?.name}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {formatNumber(inventory?.quantity)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {formatNumber(inventory?.reorder_level)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {formatCurrency(inventory?.drug?.selling_price)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/inventory/stock/reorder/${inventory?.id}/`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                          >
                            <Edit size={14} />
                            Edit Reorder
                          </Link>

                          <RoleBasedComponent allowedRoles={["superadmin", "admin"]}>
                            <Link
                              to={`/inventory/stock/reconcile/${inventory?.id}/`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors border border-teal-200"
                            >
                              <ClipboardCheck size={14} />
                              Reconcile
                            </Link>
                          </RoleBasedComponent>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-3">
                  <Box className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-medium text-gray-900">No Data Found</h3>
                <p className="text-gray-500 text-sm mt-1">
                  We couldn't find any inventory items.
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {inventoryList?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page <span className="font-medium">{currentPage}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReorderLevel;
